import React from "react";
import _ from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, Timer, Coffee, Briefcase, Info, ChevronRight, Activity } from "lucide-react";
import Datamodal from "./Datamodal";
import DatamodalAdmin from "./DatamodalAdmin";

export default function TimeList({ timesheetData, fetchTime, offset }) {
  const userProfile = useSelector((state) => state.user.userinfo);

  const hasData = !_.isEmpty(timesheetData) && timesheetData.length > 0;

  return (
    <div className="w-full px-4 sm:px-6 mt-6">
      {/* Desktop View with Row Gaps */}
      <div className="hidden lg:block space-y-4">
        {/* Header Row */}
        <div className="grid grid-cols-[1.5fr_1.2fr_1.3fr_1.2fr_1fr_1fr_0.8fr] gap-4 px-6 py-3 bg-gray-50/50 dark:bg-gray-800/40 rounded-xl border border-gray-100 dark:border-gray-800">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Team Member</div>
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest pl-2">Date & Session</div>
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest text-center">In / Out</div>
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest text-center">Working Hrs</div>
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest text-center">Break</div>
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest text-center">Required</div>
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest text-right pr-4">Details</div>
        </div>

        <div className="space-y-3">
          {hasData ? (
            <AnimatePresence mode="popLayout">
              {timesheetData.map((times, index) => (
                <motion.div
                  key={times.Id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="grid grid-cols-[1.5fr_1.2fr_1.3fr_1.2fr_1fr_1fr_0.8fr] gap-4 items-center px-6 py-4 bg-white dark:bg-gray-900/40 hover:bg-blue-50/20 dark:hover:bg-blue-900/10 rounded-2xl border border-gray-100 dark:border-blue-gray-900 shadow-sm transition-all duration-300 group relative"
                >
                  {/* Team Member */}
                  <div className="flex items-center min-w-0 pr-2">
                    <div className="relative flex-shrink-0">
                      <img
                        className="w-10 h-10 rounded-xl object-cover border border-gray-100 dark:border-gray-800 shadow-sm"
                        src={times.PhotoPath}
                        alt={times.MemberName}
                      />
                      <div className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 border-2 border-white dark:border-gray-900 rounded-full shadow-sm ${times.CheckOutTime ? "bg-gray-300" : "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]"}`} />
                    </div>
                    <div className="ps-3 min-w-0">
                      <div className="text-sm font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                        {times.MemberName}
                      </div>
                      <div className="text-[11px] text-gray-500/80 dark:text-gray-400 font-medium truncate">
                        {times.Designation}
                      </div>
                      {(times.CNIC || times.cnic) && (
                        <div className="text-[10px] font-mono text-gray-400 dark:text-gray-500 truncate mt-0.5">
                          {times.CNIC || times.cnic}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date & Session */}
                  <div className="min-w-0 flex flex-col pl-2">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Calendar className="w-3.5 h-3.5 text-blue-500/70 flex-shrink-0" />
                      <span className="text-[13px] font-bold text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {moment(times.CheckInTime).format("DD MMM YYYY")}
                      </span>
                    </div>
                    <div className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-tighter pl-5">
                      {moment(times.CheckInTime).format("dddd")}
                    </div>
                  </div>

                  {/* In / Out */}
                  <div className="flex justify-center">
                    <div className="flex items-center gap-6">
                      <div className="flex flex-col items-center">
                        <span className="text-[12px] font-bold text-green-600 dark:text-green-400">
                          {moment(times.CheckInTime).format("h:mm A")}
                        </span>
                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest mt-0.5">Check In</span>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-gray-200 mt-[-10px]" />
                      <div className="flex flex-col items-center">
                        {times.CheckOutTime ? (
                          <span className="text-[12px] font-bold text-gray-700 dark:text-gray-300">
                            {moment(times.CheckOutTime).format("h:mm A")}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[12px] font-bold text-blue-500 animate-pulse">
                            <Activity className="w-3 h-3" />
                            Active
                          </span>
                        )}
                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest mt-0.5">Check Out</span>
                      </div>
                    </div>
                  </div>

                  {/* Working Hrs */}
                  <div className="flex justify-center">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl text-blue-700 dark:text-blue-300 border border-blue-100/50 dark:border-blue-900/30">
                      <Timer className="w-3.5 h-3.5" />
                      <span className="text-xs font-black tracking-tight uppercase">
                        {times.TotalHours}h {times.TotalMins}m
                      </span>
                    </div>
                  </div>

                  {/* Break */}
                  <div className="flex justify-center">
                    <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 bg-amber-50/30 dark:bg-amber-900/5 px-3 py-1.5 rounded-xl border border-amber-100/30 dark:border-amber-900/20">
                      <Coffee className="w-3.5 h-3.5 text-amber-500/70" />
                      <span className="text-[11px] font-bold uppercase">{times.totalBreakHoursMin}</span>
                    </div>
                  </div>

                  {/* Required */}
                  <div className="flex justify-center">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-gray-50/80 dark:bg-gray-800/60 text-[11px] font-black text-gray-400 dark:text-gray-500 border border-gray-100 dark:border-gray-700/50 uppercase">
                      <Briefcase className="w-3 h-3" />
                      {times.workHours}h Req
                    </span>
                  </div>

                  {/* Details */}
                  <div className="flex justify-end pr-2">
                    {userProfile.MemberRole == 2 ? (
                      <DatamodalAdmin
                        Breaks={times?.Breaks}
                        date={moment(times.CheckInTime).format("YYYY/MM/DD")}
                        MemberId={times.MemberId}
                        fetchTime={fetchTime}
                        offset={offset}
                      />
                    ) : (
                      <Datamodal
                        Breaks={times?.Breaks}
                        date={moment(times.CheckInTime).format("YYYY/MM/DD")}
                        MemberId={times.MemberId}
                        fetchTime={fetchTime}
                        offset={offset}
                      />
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          ) : (
            <div className="py-24 text-center bg-gray-50/30 dark:bg-gray-900/20 rounded-3xl border-2 border-dashed border-gray-100 dark:border-gray-800">
              <div className="flex flex-col items-center justify-center">
                <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl shadow-sm mb-4">
                  <Activity className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 font-bold text-lg">No records found</p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Adjust your filters to see more results</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4 pb-8">
        {hasData ? (
          timesheetData.map((times, index) => (
            <motion.div
              key={times.Id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white dark:bg-gray-900/60 backdrop-blur-md rounded-2xl p-5 border border-gray-100 dark:border-blue-gray-900 shadow-sm active:scale-[0.98] transition-all"
            >
              {userProfile.MemberRole == 2 && (
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <img className="w-12 h-12 rounded-xl object-cover shadow-sm border border-gray-100 dark:border-gray-800" src={times.PhotoPath} alt={times.MemberName} />
                    <div>
                      <div className="text-base font-bold text-gray-900 dark:text-white">
                        {times.MemberName}
                      </div>
                      <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                        {times.Designation}
                      </div>
                      {(times.CNIC || times.cnic) && (
                        <div className="text-[10px] font-mono text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-[180px]">
                          {times.CNIC || times.cnic}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${times.CheckOutTime ? "bg-gray-100 text-gray-500" : "bg-green-100/50 text-green-600 border border-green-200/50"}`}>
                    {times.CheckOutTime ? "Completed" : "Active Now"}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="space-y-1">
                  <div className="text-[10px] text-gray-400 uppercase font-bold tracking-widest flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Date
                  </div>
                  <div className="text-sm font-bold text-gray-800 dark:text-gray-200">
                    {moment(times.CheckInTime).format("DD MMM YYYY")}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] text-gray-400 uppercase font-bold tracking-widest flex items-center gap-1">
                    <Timer className="w-3 h-3" /> Working Hrs
                  </div>
                  <div className="text-sm font-bold text-blue-600 dark:text-blue-400">
                    {times.TotalHours}h {times.TotalMins}m
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] text-gray-400 uppercase font-bold tracking-widest flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Check In
                  </div>
                  <div className="text-sm font-bold text-gray-800 dark:text-gray-200">
                    {moment(times.CheckInTime).format("h:mm A")}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] text-gray-400 uppercase font-bold tracking-widest flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Check Out
                  </div>
                  <div className={`text-sm font-bold ${times.CheckOutTime ? "text-gray-800 dark:text-gray-200" : "text-green-500 italic"}`}>
                    {times.CheckOutTime ? moment(times.CheckOutTime).format("h:mm A") : "Still Active"}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-50 dark:border-gray-800/60">
                <div className="flex gap-4">
                  <div className="flex items-center gap-1.5">
                    <Coffee className="w-3.5 h-3.5 text-amber-500/70" />
                    <span className="text-[10px] font-bold text-gray-500 uppercase">{times.totalBreakHoursMin}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-[10px] font-bold text-gray-500 uppercase">{times.workHours}h req</span>
                  </div>
                </div>
                <div className="scale-90 origin-right">
                  {userProfile.MemberRole == 2 ? (
                    <DatamodalAdmin
                      Breaks={times?.Breaks}
                      date={moment(times.CheckInTime).format("YYYY/MM/DD")}
                      MemberId={times.MemberId}
                      fetchTime={fetchTime}
                      offset={offset}
                    />
                  ) : (
                    <Datamodal
                      Breaks={times?.Breaks}
                      date={moment(times.CheckInTime).format("YYYY/MM/DD")}
                      MemberId={times.MemberId}
                      fetchTime={fetchTime}
                      offset={offset}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="text-center py-20 bg-gray-50/50 dark:bg-gray-900/20 rounded-3xl border-2 border-dashed border-gray-100 dark:border-gray-800">
            <Info className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 font-bold">No results found.</p>
          </div>
        )}
      </div>
    </div>
  );
}
